import React, { memo, useMemo, useState } from 'react'
import { Avatar, Button, Popover } from 'antd'
import { Icon } from '@iconify/react'

import DateRangePicker from '../../../projects/components/DateRangePicker'
import ProjectMultiSelect from '../../../../components/ProjectMultiSelect'
import { REPORTING_DATE_DISPLAY_FORMAT } from '../../reporting.constants'
import { getInitials, REPORTING_PERIOD } from '../workload.utils'
import styles from '../WorkloadDashboard.module.css'

const WorkloadFilters = memo(
    ({
        projectIds = [],
        projects = [],
        onProjectChange,
        onProjectSearch,
        onProjectPopupScroll,
        projectListLoading = false,
        projectListPage = 1,
        onProjectOpenChange,
        userIds = [],
        users = [],
        onUserChange,
        onUserSearch,
        onUserPopupScroll,
        userListLoading = false,
        userListPage = 1,
        onUserOpenChange,
        periodType,
        start,
        end,
        onDateRangeChange,
        onClearFilters,
    }) => {
        const [filterPopoverOpen, setFilterPopoverOpen] = useState(false)

        const projectFilterCount = Array.isArray(projectIds)
            ? projectIds.length
            : 0
        const userFilterCount = Array.isArray(userIds) ? userIds.length : 0
        const hasDateFilter = periodType !== REPORTING_PERIOD.DAY
        const activeFilterCount =
            projectFilterCount + userFilterCount + (hasDateFilter ? 1 : 0)
        const hasActiveFilters = activeFilterCount > 0

        const rangeValue = useMemo(
            () => ({
                start,
                end,
            }),
            [start, end]
        )

        const userSelectOptions = useMemo(
            () =>
                (users || [])
                    .map((user) => {
                        const value = user?._id ?? user?.value ?? null
                        if (!value) return null
                        return {
                            value,
                            label: user?.name || user?.email || 'Untitled',
                            email: user?.email || '',
                            profilePicture: user?.profilePicture || '',
                        }
                    })
                    .filter(Boolean),
            [users]
        )

        const filterPopoverContent = useMemo(
            () => {
                const renderUserOption = (option) => {
                    const data = option?.data || option || {}
                    const name = data.label || 'Untitled'
                    const email = data.email || ''
                    const profilePicture = data.profilePicture || ''

                    return (
                        <div className={styles.userFilterOption}>
                            <Avatar
                                size={28}
                                src={profilePicture || undefined}
                                className={styles.userFilterAvatar}
                            >
                                {!profilePicture ? getInitials(name) : null}
                            </Avatar>
                            <div className={styles.userMeta}>
                                <div className={styles.userName}>{name}</div>
                                <div className={styles.userEmail}>
                                    {email || '—'}
                                </div>
                            </div>
                        </div>
                    )
                }

                return (
                <div className={styles.filterPopoverPanel}>
                    <div className={styles.filterPopoverHeader}>
                        <span className={styles.filterPopoverTitle}>
                            Filters
                        </span>
                        <button
                            type="button"
                            className={`${styles.filterPopoverClear} ${
                                !hasActiveFilters
                                    ? styles.filterPopoverClearDisabled
                                    : ''
                            }`}
                            disabled={!hasActiveFilters}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                if (!hasActiveFilters) return
                                onClearFilters?.()
                                setFilterPopoverOpen(true)
                            }}
                        >
                            Clear
                        </button>
                    </div>

                    <div className={styles.filterPopoverSection}>
                        <div className={styles.filterPopoverLabel}>
                            Date range
                        </div>
                        <div className={styles.filterPopoverDateControls}>
                            <div className={styles.dateFilterWrap}>
                                <DateRangePicker
                                    value={rangeValue}
                                    onChange={onDateRangeChange}
                                    placeholder="Date range"
                                    parentComponent="taskDetailView"
                                    dateFormat={REPORTING_DATE_DISPLAY_FORMAT}
                                    showClear={false}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.filterPopoverSection}>
                        <div className={styles.filterPopoverLabel}>
                            Filter by Projects
                        </div>
                        <ProjectMultiSelect
                            wrapClassName={styles.filterPopoverProjectSelect}
                            projects={projects}
                            value={projectIds}
                            onChange={(ids) => onProjectChange?.(ids || [])}
                            placeholder="Select Projects"
                            showSearch
                            filterOption={false}
                            onSearch={onProjectSearch}
                            onPopupScroll={onProjectPopupScroll}
                            loading={projectListLoading}
                            page={projectListPage}
                            onOpenChange={onProjectOpenChange}
                        />
                    </div>

                    <div className={styles.filterPopoverSection}>
                        <div className={styles.filterPopoverLabel}>
                            Filter by Users
                        </div>
                        <ProjectMultiSelect
                            wrapClassName={styles.filterPopoverProjectSelect}
                            options={userSelectOptions}
                            value={userIds}
                            onChange={(ids) => onUserChange?.(ids || [])}
                            placeholder="Select Users"
                            emptyText="No users found"
                            showSearch
                            filterOption={false}
                            onSearch={onUserSearch}
                            onPopupScroll={onUserPopupScroll}
                            loading={userListLoading}
                            page={userListPage}
                            onOpenChange={onUserOpenChange}
                            optionRender={renderUserOption}
                            popupClassName={styles.userFilterDropdown}
                        />
                    </div>
                </div>
                )
            },
            [
                hasActiveFilters,
                onClearFilters,
                rangeValue,
                onDateRangeChange,
                projects,
                projectIds,
                onProjectChange,
                onProjectSearch,
                onProjectPopupScroll,
                projectListLoading,
                projectListPage,
                onProjectOpenChange,
                userSelectOptions,
                userIds,
                onUserChange,
                onUserSearch,
                onUserPopupScroll,
                userListLoading,
                userListPage,
                onUserOpenChange,
            ]
        )

        return (
            <div
                className={
                    hasActiveFilters ? styles.filterBadge : styles.filterTrigger
                }
            >
                <Popover
                    arrow={false}
                    content={filterPopoverContent}
                    title={null}
                    trigger="click"
                    open={filterPopoverOpen}
                    onOpenChange={setFilterPopoverOpen}
                    placement="bottomRight"
                    overlayClassName={styles.filtersPopover}
                >
                    {hasActiveFilters ? (
                        <div
                            className={styles.filterBadgeContent}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    setFilterPopoverOpen(true)
                                }
                            }}
                        >
                            <Icon
                                icon="mynaui:filter"
                                width={16}
                                height={16}
                                className={styles.filterBadgeIcon}
                            />
                            <span className={styles.filterBadgeText}>
                                Filters: {activeFilterCount}
                            </span>
                        </div>
                    ) : (
                        <Button
                            type="text"
                            className={styles.filterToolbarButton}
                            icon={
                                <Icon
                                    icon="mynaui:filter"
                                    width={18}
                                    height={18}
                                />
                            }
                        >
                            Filter
                        </Button>
                    )}
                </Popover>
                {hasActiveFilters ? (
                    <button
                        className={styles.filterBadgeClose}
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            onClearFilters?.()
                        }}
                        type="button"
                        aria-label="Clear filters"
                    >
                        <Icon
                            icon="mdi:close"
                            width={14}
                            height={14}
                            className={styles.filterBadgeCloseIcon}
                        />
                    </button>
                ) : null}
            </div>
        )
    }
)

WorkloadFilters.displayName = 'WorkloadFilters'

export default WorkloadFilters
